import { Text } from '@loomhq/lens';
import React, { useEffect, useState } from 'react';

import { SHARE_PAGE_EDITOR_FAB_PRO_UPSELL } from 'constants/events';
import * as analytics from 'utilities/analytics';

import CheckIcon from 'img/icons/new/check-neg-rp.svg';
import DropdownIcon from 'img/icons/new/dropdown-pb.svg';
import PublicIcon from 'img/icons/new/globe-pb.svg';
import LinkIcon from 'img/icons/new/link-pg.svg';

import ActionDot, { TOP_LEFT } from 'components/ui-components/action-dot';
import Button from 'components/ui-components/button';
import Dropdown from 'components/ui-components/dropdown';
import Tooltip from 'components/ui-components/tooltip';

import './styles.less';

const LINK_SHARING_KEY = 'link-sharing';
const PUBLIC_VIDEO_KEY = 'public';

// TODO(@harsh): add (default) to whatever the a/b test say works fine
const DROPDOWN_OPTIONS = [
  {
    key: LINK_SHARING_KEY,
    title: 'Link Sharing',
    description: 'Only people with the link can see your video',
    icon: LinkIcon,
  },
  {
    key: PUBLIC_VIDEO_KEY,
    title: 'Public',
    description:
      'Can appear in Google search. Great for sharing your video with many.',
    icon: PublicIcon,
  },
];

function PrivacySection(props) {
  const [dropdown, setShowDropdown] = useState(null);
  const [selection, setSelection] = useState(() => {
    const { video } = props;

    return video && video.allow_search_engine_indexing
      ? PUBLIC_VIDEO_KEY
      : LINK_SHARING_KEY;
  });

  const selectedOption = DROPDOWN_OPTIONS.find(opt => opt.key === selection);

  async function selectAndCloseDropdown(key) {
    setShowDropdown(false);

    const { video } = props;
    const isSetToPublic = video && video.allow_search_engine_indexing;
    const setToPublic = key === PUBLIC_VIDEO_KEY;

    if (setToPublic !== isSetToPublic) {
      await props.onVideoPrivacyChange(setToPublic);
    }

    setSelection(key);
  }

  function handleUpgradeUpsell() {
    analytics.track(SHARE_PAGE_EDITOR_FAB_PRO_UPSELL, {
      fab_type: 'custom-viewer',
    });
    props.showUpgradeModal();
  }

  const {
    expanded,
    video,
    hasPro,
    toggleExpansion,
    customAccessTourIsActive,
    addPasswordTourIsActive,
    searchIndexingTourIsActive,
  } = props;
  const { is_protected, permitted_viewers_only } = video;

  useEffect(() => {
    const tourActive =
      addPasswordTourIsActive ||
      customAccessTourIsActive ||
      searchIndexingTourIsActive;

    if (tourActive) {
      toggleExpansion(tourActive);
    }
  }, [
    toggleExpansion,
    addPasswordTourIsActive,
    customAccessTourIsActive,
    searchIndexingTourIsActive,
  ]);

  return (
    <div className="video-privacy-section">
      <div className="header-section">
        <Text variant="title">Privacy</Text>
        <div
          className="header-collapse-section"
          onClick={() => props.toggleExpansion()}
        >
          {expanded ? 'Hide' : 'Manage'}
        </div>
      </div>
      {expanded && (
        <div className="controls-section">
          <div className="privacy-options-drowdown-wrapper">
            {!permitted_viewers_only && (
              <>
                <div
                  className="selected-privacy-option"
                  onClick={() => setShowDropdown(!dropdown)}
                >
                  {searchIndexingTourIsActive && (
                    <ActionDot position={TOP_LEFT} />
                  )}
                  <img src={selectedOption.icon} className="icon" />
                  <div className="text-section">
                    <div className="title">{selectedOption.title}</div>
                    <div className="subtitle">{selectedOption.description}</div>
                  </div>
                  <img src={DropdownIcon} className="dropdown-icon" />
                </div>
                <Dropdown
                  show={dropdown}
                  outsideClickIgnoreClass="selected-privacy-option"
                  onClickOutside={() => setShowDropdown(false)}
                >
                  {DROPDOWN_OPTIONS.map(({ key, title, icon, description }) => (
                    <div
                      className="selected-privacy-option"
                      key={key}
                      onClick={() => selectAndCloseDropdown(key)}
                    >
                      <img src={icon} className="icon" />
                      <div className="text-section">
                        <div className="title">{title}</div>
                        <div className="subtitle">{description}</div>
                      </div>
                      {key === selectedOption.key && (
                        <img src={CheckIcon} className="checkmark" />
                      )}
                    </div>
                  ))}
                </Dropdown>
              </>
            )}
            {permitted_viewers_only && (
              <div className="selected-privacy-option">
                <div className="text-section">
                  <div className="title">Custom Access Enabled</div>
                  <div className="subtitle">
                    For public access or link sharing,{' '}
                    <span className="cta" onClick={props.onCustomViewerClick}>
                      remove access
                    </span>
                    .
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="privacy-control-option-buttons">
            <div className="privacy-control-button-tooltip-wrapper">
              {customAccessTourIsActive && <ActionDot position={TOP_LEFT} />}
              {hasPro && (
                <Tooltip>
                  Only allow people with specific emails
                  <br /> to view this video.
                </Tooltip>
              )}
              {!hasPro && (
                <Tooltip stickyOnHover={true}>
                  <div>
                    Invite people by email to limit who
                    <br /> is able to watch your video.
                  </div>
                  <Button
                    onClicked={handleUpgradeUpsell}
                    outerClass="tooltip-upgrade-button"
                    value="Upgrade Now"
                    buttonClass="rp-primary-4 premium"
                  />
                </Tooltip>
              )}
              <Button
                disabled={!hasPro}
                buttonClass="rp-secondary-3"
                value="Invite People"
                onClicked={props.onCustomViewerClick}
              />
            </div>
            <div className="privacy-control-button-tooltip-wrapper">
              {addPasswordTourIsActive && <ActionDot position={TOP_LEFT} />}
              <Tooltip>
                Only allow people with a password <br />
                to view this video
              </Tooltip>
              <Button
                outerClass="add-password"
                buttonClass="rp-secondary-3"
                value={is_protected ? 'Edit Password' : 'Add Password'}
                onClicked={props.onAddPasswordClick}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrivacySection;
